<template>
	<div class="container-fluid p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div>
				<h1 class="mb-0">Products</h1>
				<p class="text-muted mb-0">
					Your district's own pesticide products, adopted from the shared,
					curated catalog. A product the catalog does not carry is
					<strong>requested, never created</strong> by the district.
				</p>
			</div>
			<button class="btn btn-primary" @click="requestOpen = true">
				<i class="bi bi-send me-1"></i>Request a product
			</button>
		</div>

		<!-- The SDS book: one dated PDF of the district's labels and safety data
		     sheets. The card states the gaps before the download is offered and
		     links each incomplete brand to its district product, where the
		     catalog entry's documents and the brand's SDS slot are shown. -->
		<div class="card mb-4">
			<div class="card-body">
				<div
					class="d-flex justify-content-between align-items-start gap-3 flex-wrap"
				>
					<div>
						<h2 class="h5 mb-1">SDS book</h2>
						<p class="text-muted mb-0 small">
							Every product's label, then its safety data sheet, in one dated
							PDF, alphabetically by brand. A document two brands share is
							included once.
							<template v-if="bookSummary">
								This book would contain
								<strong>{{ bookSummary.brands }}</strong>
								{{ bookSummary.brands === 1 ? "brand" : "brands" }} and
								<strong>{{ bookSummary.documents }}</strong>
								{{ bookSummary.documents === 1 ? "document" : "documents" }}.
							</template>
							<template v-else-if="bookSummaryError">
								{{ bookSummaryError }}
							</template>
						</p>
					</div>
					<button
						class="btn btn-primary"
						:disabled="bookLoading"
						@click="generateBook"
					>
						<span
							v-if="bookLoading"
							class="spinner-border spinner-border-sm me-1"
							role="status"
							aria-hidden="true"
						></span>
						<i v-else class="bi bi-file-earmark-pdf me-1"></i>
						{{ bookLoading ? "Generating…" : "Generate SDS book" }}
					</button>
				</div>

				<div
					v-if="bookSummary && missingGaps.length > 0"
					class="alert alert-warning mt-3 mb-0"
				>
					<p class="mb-2">
						<i class="bi bi-exclamation-triangle me-1"></i>
						This book is <strong>incomplete</strong>:
						<template v-if="bookSummary.missing_labels.length > 0">
							{{ bookSummary.missing_labels.length }}
							{{
								bookSummary.missing_labels.length === 1
									? "brand has"
									: "brands have"
							}}
							no label
						</template>
						<template
							v-if="
								bookSummary.missing_labels.length > 0 &&
								bookSummary.missing_sds.length > 0
							"
						>
							and
						</template>
						<template v-if="bookSummary.missing_sds.length > 0">
							{{ bookSummary.missing_sds.length }}
							{{
								bookSummary.missing_sds.length === 1
									? "brand has"
									: "brands have"
							}}
							no safety data sheet </template
						>. The generated front page lists them, and the download still
						contains every document the district does have. Open a product to
						fill the gap.
					</p>
					<div class="d-flex flex-wrap gap-2">
						<RouterLink
							v-for="gap in missingGaps"
							:key="`${gap.what}-${gap.product_id}-${gap.name}`"
							class="btn btn-sm btn-outline-warning"
							:to="`/_/inventory/products/${gap.product_id}`"
							:title="`Open ${gap.product_name} to file the missing ${gap.what}`"
						>
							{{ gap.name }}
							<span class="badge text-bg-warning ms-1">{{ gap.what }}</span>
						</RouterLink>
					</div>
				</div>
				<div
					v-else-if="bookSummary"
					class="alert alert-success mt-3 mb-0 small"
				>
					<i class="bi bi-check-circle me-1"></i>
					Every brand in this book has its label and its safety data sheet.
				</div>
			</div>
		</div>

		<!-- District Products: the district's own list and the terms it holds.
		     The shared catalog is the sibling page this button opens; adding a
		     product happens there, never here. -->
		<div class="card">
			<div class="card-body">
				<div
					class="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3"
				>
					<div>
						<h2 class="h5 mb-0">District Products</h2>
						<p class="text-muted mb-0 small">
							Your district's own products. Edits save automatically; the
							control class drives each treatment's Method.
						</p>
					</div>
					<div class="d-flex align-items-center gap-3">
						<div class="form-check form-switch mb-0">
							<input
								id="onlyCatalogUpdates"
								v-model="onlyUpdates"
								class="form-check-input"
								type="checkbox"
								@change="loadDistrictProducts"
							/>
							<label class="form-check-label small" for="onlyCatalogUpdates">
								Needs catalog update
							</label>
						</div>
						<SaveIndicator
							:state="productSaveState"
							:pending="productPending"
							@retry="retryProductSave"
						/>
						<RouterLink
							class="btn btn-outline-primary"
							to="/_/inventory/catalog"
						>
							<i class="bi bi-journal-text me-1"></i>View full catalog
						</RouterLink>
					</div>
				</div>

				<ProductTable
					:rows="productRows"
					variant="district"
					:loading="districtProductsLoading"
					:error="districtProductsError"
					empty-text="No district products yet. Add one from the shared catalog."
					@update-class="updateProductClass"
					@update-residual="updateProductResidual"
					@preview-catalog-update="openCatalogUpdate"
				/>

				<!-- The district-side preview. It shows what the catalog now says
				     against this row, field by field, and marks the fields the
				     district set itself; accepting applies only the fields the
				     district selects. -->
				<div v-if="preview" class="card border-warning mt-3">
					<div
						class="card-header d-flex justify-content-between align-items-center"
					>
						<div>
							<i class="bi bi-arrow-up-circle me-1"></i>
							Catalog update: {{ preview.catalog_name }}
							<span class="badge text-bg-secondary ms-2">
								revision {{ preview.district_revision ?? 0 }} of
								{{ preview.catalog_revision }}
							</span>
						</div>
						<button
							type="button"
							class="btn-close"
							aria-label="Close"
							@click="preview = null"
						></button>
					</div>
					<div class="card-body">
						<p class="text-muted small mb-3">
							The catalog is {{ preview.revisions_behind }} revision(s) ahead;
							this district has been behind for
							{{ formatLag(preview.lag_seconds) }}. Fields marked
							<em>district-set</em> were changed here, not by the catalog;
							accepting a field replaces the district's value.
						</p>
						<div class="table-responsive">
							<table class="table table-sm align-middle mb-3">
								<thead class="table-light">
									<tr>
										<th scope="col">
											<span class="visually-hidden">Accept</span>
										</th>
										<th scope="col">Field</th>
										<th scope="col">Catalog says</th>
										<th scope="col">District has</th>
										<th scope="col">District-set</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="field in preview.fields" :key="field.field">
										<td>
											<input
												v-model="selectedFields"
												class="form-check-input"
												type="checkbox"
												:value="field.field"
												:disabled="field.catalog_value === ''"
											/>
										</td>
										<th scope="row" class="font-monospace small">
											{{ field.field }}
										</th>
										<td>{{ field.catalog_value || "—" }}</td>
										<td>{{ field.district_value || "—" }}</td>
										<td>
											<span
												v-if="field.district_modified"
												class="badge text-bg-warning"
												>district-set</span
											>
											<span v-else class="text-muted small">—</span>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
						<div class="d-flex gap-2">
							<button
								class="btn btn-primary btn-sm"
								:disabled="selectedFields.length === 0 || accepting"
								@click="acceptSelected"
							>
								<span
									v-if="accepting"
									class="spinner-border spinner-border-sm me-1"
								></span>
								Accept selected ({{ selectedFields.length }})
							</button>
							<button
								class="btn btn-outline-secondary btn-sm"
								@click="preview = null"
							>
								Close
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<ProductRequestModal
			:open="requestOpen"
			@close="requestOpen = false"
			@created="requestCreated"
		/>
		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import SaveIndicator from "@/components/common/SaveIndicator.vue";
import ProductRequestModal from "@/components/pesticide/ProductRequestModal.vue";
import ProductTable from "@/components/pesticide/ProductTable.vue";
import { buildDistrictRow } from "@/components/pesticide/productTable";
import ToastNotification from "@/components/ToastNotification.vue";
import { apiClient } from "@/client";
import { useSaveState } from "@/lib/autoSave";
import { useStoreProduct } from "@/store/product";
import type {
	CatalogUpdatePreview,
	InventorySdsBookSummary,
	PesticideCatalogRequest,
} from "@/type/api";

const storeProduct = useStoreProduct();
const districtProducts = computed(() => storeProduct.products);
const districtProductsLoading = computed(() => storeProduct.loading);
const districtProductsError = ref("");
const onlyUpdates = ref(false);
const preview = ref<CatalogUpdatePreview | null>(null);
const selectedFields = ref<string[]>([]);
const accepting = ref(false);
const requestOpen = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);
const bookSummary = ref<InventorySdsBookSummary | null>(null);
const bookSummaryError = ref("");
const bookLoading = ref(false);
const {
	state: productSaveState,
	pending: productPending,
	run: runProductSave,
	retry: retryProductSave,
} = useSaveState("pesticide-products");

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

// requestCreated confirms the district's ask; the request goes to the curator's
// queue, and the catalog gains nothing until a curator promotes the product.
function requestCreated(request: PesticideCatalogRequest) {
	showNotification(
		"Request sent",
		`Request #${request.id} is with the catalog curators. A product the catalog does not carry is requested, never created here; you will see it in the catalog once a curator promotes it.`,
	);
}

const productRows = computed(() =>
	districtProducts.value.map(buildDistrictRow),
);

async function loadDistrictProducts() {
	districtProductsError.value = "";
	try {
		await storeProduct.list({ catalogUpdate: onlyUpdates.value });
	} catch (e: any) {
		districtProductsError.value = e?.message || "Failed to load products.";
	}
}

// missingGaps flattens the summary's two lists into the chips the card shows.
// A brand missing both documents appears twice, once per document, because the
// two are filled in different places.
const missingGaps = computed(() => {
	const summary = bookSummary.value;
	if (!summary) return [];
	return [
		...summary.missing_labels.map((gap) => ({ ...gap, what: "label" })),
		...summary.missing_sds.map((gap) => ({ ...gap, what: "SDS" })),
	];
});

// loadBookSummary asks the server what the book would contain. It is the same
// assembly the download runs, so the counts and gaps shown here cannot disagree
// with the PDF: a brand listed here is listed on its front page.
async function loadBookSummary() {
	bookSummaryError.value = "";
	try {
		bookSummary.value = await apiClient.JSONGet<InventorySdsBookSummary>(
			"/api/inventory/sds-book/summary",
		);
	} catch (e: any) {
		bookSummary.value = null;
		bookSummaryError.value =
			"The book summary is unavailable; the download still works.";
	}
}

// sdsBookFilename mirrors the server's naming, so a saved file reads the same
// whether it was fetched here or opened from the route directly. The date is
// the summary's generation date (server time) when it is known.
function sdsBookFilename(): string {
	const district = slugify(bookSummary.value?.district ?? "") || "district";
	const generated = (
		bookSummary.value?.generated_at ?? new Date().toISOString()
	).slice(0, 10);
	return `${district}-sds-book-${generated}.pdf`;
}

// slugify reduces a district name to lowercase words joined by hyphens.
function slugify(value: string): string {
	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
}

// generateBook downloads the district's book. It is generated on demand rather
// than cached, so the date on its front page is the day the operator asked for
// it; the request gets a longer timeout than the client's default because a
// district with many labels makes a large PDF.
async function generateBook() {
	if (bookLoading.value) return;
	bookLoading.value = true;
	try {
		const blob = await apiClient.JSONGet<Blob>("/api/inventory/sds-book", {
			responseType: "blob",
			timeout: 180000,
		});
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement("a");
		anchor.href = url;
		anchor.download = sdsBookFilename();
		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
		URL.revokeObjectURL(url);
		showNotification("SDS book ready", anchor.download);
	} catch (e: any) {
		showNotification(
			"Failed to generate the SDS book",
			e?.message || "Something went wrong.",
		);
	} finally {
		bookLoading.value = false;
	}
}

// formatLag renders the server's lag_seconds; the client never computes the
// lag itself.
function formatLag(seconds: number): string {
	if (seconds < 3600) return Math.round(seconds / 60) + " minutes";
	if (seconds < 86400) return Math.round(seconds / 3600) + " hours";
	return Math.round(seconds / 86400) + " days";
}

// openCatalogUpdate asks the server for the field-by-field comparison: what the
// catalog says now against this row, with the fields the district changed
// marked. It pre-selects the fields the catalog actually moved.
async function openCatalogUpdate(id: number) {
	districtProductsError.value = "";
	try {
		const result = await storeProduct.catalogUpdatePreview(id);
		preview.value = result;
		selectedFields.value = result.fields
			.filter((field) => field.changed && field.catalog_value !== "")
			.map((field) => field.field);
	} catch (e: any) {
		districtProductsError.value =
			e?.message || "Failed to load the catalog update.";
	}
}

async function acceptSelected() {
	if (!preview.value || selectedFields.value.length === 0) return;
	accepting.value = true;
	try {
		preview.value = await storeProduct.acceptCatalogUpdate(
			preview.value.product_id,
			selectedFields.value,
		);
		selectedFields.value = [];
		await loadDistrictProducts();
	} catch (e: any) {
		districtProductsError.value =
			e?.message || "Failed to accept the catalog update.";
	} finally {
		accepting.value = false;
	}
}

// A class change is one whole value and saves at once.
async function updateProductClass(id: number, value: string) {
	const product = districtProducts.value.find((row) => row.id === id);
	if (!product) return;
	product.control_class = value;
	await runProductSave(async () => {
		await storeProduct.save(id, { control_class: value });
	});
}

// A residual is a whole, non-negative day count; an empty box clears it. A
// value that is neither is left unsaved rather than sent, so a typo cannot
// silently become NULL on the wire.
async function updateProductResidual(id: number, raw: string) {
	const product = districtProducts.value.find((row) => row.id === id);
	if (!product) return;
	const trimmed = raw.trim();
	let days: number | null = null;
	if (trimmed !== "") {
		const parsed = Number(trimmed);
		if (!Number.isInteger(parsed) || parsed < 0) return;
		days = parsed;
	}
	product.residual_days = days;
	await runProductSave(async () => {
		await storeProduct.save(id, { residual_days: days });
	});
}

onMounted(() => {
	loadDistrictProducts();
	loadBookSummary();
});
</script>
